import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ClipboardList, DollarSign, TrendingDown, ShieldCheck, ShieldAlert } from "lucide-react";
import { api, money, dateShort } from "../lib/api";

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div
    data-testid={testId}
    className="bg-[#12100E] border border-white/5 p-6 hover:border-[#A66E38]/40 transition-colors"
  >
    <div className="flex items-center justify-between mb-3">
      <Icon size={20} className="text-[#C89B3C]" />
      <span className="text-[0.6rem] uppercase tracking-[0.4em] text-[#8A7D71]">
        {label}
      </span>
    </div>
    <p className="font-serif text-3xl md:text-4xl font-black text-[#F5F0E6]">
      {value}
    </p>
    {sub && <p className="text-xs text-[#8A7D71] mt-2">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/admin/dashboard")
      .then(setData)
      .catch((e) => setErr(e.message));
  }, []);

  if (err) return <p className="text-red-300" data-testid="dashboard-error">{err}</p>;
  if (!data) return <p className="text-[#8A7D71]" data-testid="dashboard-loading">Cargando...</p>;

  return (
    <div data-testid="dashboard-view" className="space-y-10">
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-[#A66E38] mb-3">
          Vista general
        </p>
        <h1 className="font-serif text-4xl md:text-5xl font-black text-[#F5F0E6]">
          Dashboard
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat
          icon={Users}
          label="Clientes"
          value={data.counts.customers}
          testId="stat-customers"
        />
        <Stat
          icon={ClipboardList}
          label="Apartados activos"
          value={data.counts.active_layaways}
          sub={`${data.counts.completed_layaways} completados`}
          testId="stat-active-layaways"
        />
        <Stat
          icon={DollarSign}
          label="Cobrado"
          value={money(data.money.paid_total)}
          testId="stat-paid"
        />
        <Stat
          icon={TrendingDown}
          label="Por cobrar"
          value={money(data.money.outstanding_total)}
          testId="stat-outstanding"
        />
      </div>

      <div
        data-testid="valor-status"
        className={`p-5 border ${
          data.valor_configured
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-amber-500/30 bg-amber-500/5"
        } flex items-start gap-3`}
      >
        {data.valor_configured ? (
          <ShieldCheck className="text-emerald-400 mt-0.5" size={20} />
        ) : (
          <ShieldAlert className="text-amber-400 mt-0.5" size={20} />
        )}
        <div>
          <p className="font-semibold text-[#F5F0E6]">
            Valor Virtual Terminal:{" "}
            <span className={data.valor_configured ? "text-emerald-300" : "text-amber-300"}>
              {data.valor_configured ? `Conectado (${data.valor_env})` : "No configurado"}
            </span>
          </p>
          <p className="text-sm text-[#B8A99A] mt-1">
            {data.valor_configured
              ? "Puedes cobrar tarjetas desde los apartados."
              : "Agrega VALOR_APP_ID, VALOR_APP_KEY y VALOR_EPI en el backend .env y reinicia. Mientras tanto los pagos en efectivo funcionan."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section data-testid="recent-layaways" className="bg-[#12100E] border border-white/5 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#F5F0E6]">Apartados recientes</h2>
            <Link to="/admin/layaways" className="text-xs uppercase tracking-widest text-[#C89B3C] hover:underline">
              Ver todos
            </Link>
          </div>
          {data.recent_layaways.length === 0 ? (
            <p className="text-sm text-[#8A7D71]">Sin apartados aún.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.recent_layaways.map((l) => (
                <li key={l.id} className="py-3 flex items-center justify-between">
                  <div>
                    <Link
                      to={`/admin/layaways/${l.id}`}
                      className="text-[#F5F0E6] hover:text-[#C89B3C] transition-colors"
                    >
                      {l.invoice_number}
                    </Link>
                    <p className="text-xs text-[#8A7D71]">
                      {dateShort(l.created_at)} · {l.status}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#F5F0E6]">{money(l.total_amount)}</p>
                    <p className="text-xs text-[#8A7D71]">
                      Pagado {money(l.paid_amount)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="recent-payments" className="bg-[#12100E] border border-white/5 p-6">
          <h2 className="font-serif text-2xl text-[#F5F0E6] mb-5">
            Pagos recientes
          </h2>
          {data.recent_payments.length === 0 ? (
            <p className="text-sm text-[#8A7D71]">Sin pagos registrados.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.recent_payments.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-[#F5F0E6]">{money(p.amount)}</p>
                    <p className="text-xs text-[#8A7D71]">
                      {dateShort(p.created_at)} · {p.method}
                    </p>
                  </div>
                  <span
                    className={`text-xs uppercase tracking-widest px-2 py-1 ${
                      p.status === "aprobado"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : p.status === "rechazado"
                        ? "bg-red-500/15 text-red-300"
                        : "bg-amber-500/15 text-amber-300"
                    }`}
                  >
                    {p.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
